import { NavigationEnd, Router } from "@angular/router";
import { Injectable } from "@angular/core";
import {
	MarginLeverageDirection,
	MarginPositionType,
	OrderTypeTrackingProperty,
	TradeTypeTrackingProperty
} from "@modules/checkout/entities/trade.rudderstack.tracking.model";

export interface SegmentDynamicProperties {
	fiat_currency?: string;
	asset_category?: string;
	deposit_method?: string;
	deposit_amount?: string;
	amount?: string;
	bfc_enabled?: boolean;
	bfc_status?: string;
	asset_currency_source?: string;
	asset_currency_destination?: string;
	savings_method?: string;
	withdrawal_method?: string;
	withdrawal_amount?: string;
	send_amount?: string;
	trade_by?: string;
	button_amount?: string;
	currency?: string;
	fees_selection?: string;
	flow?: string;
	state?: string;
	category?: string;
	label?: string;
	asset?: string;
	asset_id?: string;
	screen_it_happened_on?: string;
	time_filter_selected?: string;
	time?: string;
	asset_class_type?: string;
	all_selected_filters?: string[];
	sorting_option?: string;
	recommendation_card?: string;
	trade_asset_amount?: string;
	trade_fiat_amount?: string;
	event_name?: string;
	preference_spotlight_asset?: string;
	preference_spotlight_asset_value?: boolean;
	preference_spotlight_general?: boolean;
	asset_symbol?: string;
	network?: string;
	filter?: string;
	payment_method?: string;
	payment_method_id?: string;
	selected_amount_type?: string;
	fiat_id?: string;
	story_group?: number;
	story?: number;
	trigger?: string;
	deposit_option?: string;
	flow_it_happened_on?: string;
	slice?: number;
	flow_triggered_by?: string;
	user_pid?: string;
	type?: string;
	order_type?: OrderTypeTrackingProperty;
	trade_type?: TradeTypeTrackingProperty;
	triggered_by?: string;
	cancellation_status?: string;
	asset_type?: string;
	fund_wallet_button_viewed?: boolean;
	new_order_button_viewed?: boolean;
	status?: string;
	touchpoint?: string;
	exit_source?: string;
	apc_status?: string;
	utm_medium?: string;
	utm_campaign?: string;
	destination?: string;
	percentage?: number;
	unit?: string;
	trade_status?: string;
	source_symbol?: string;
	destination_symbol?: string;
	first_trader?: boolean;
	phase?: string;
	opt_in?: boolean;
	report_type?: string;
	entry_point?: string;
	download_entry_point?: string;
	report_id?: string;
	undeclared_deposits?: boolean;
	user_status?: string;
	migration_estimate?: number;
	bundle_id?: string[];
	vision_banner_type?: string;
	trade_amount_asset?: number;
	trade_amount_EUR?: number;
	country_of_residence?: string;
	bitpanda_platform?: string;
	user_type?: string;
	order_id?: string;
	revenue?: number;
	products?: unknown;
	option?: string;
	margin_position_type?: MarginPositionType;
	margin_leverage_type?: MarginLeverageDirection;
	is_shown?: boolean;
	amount_unit_type?: string;
	amount_unit?: string;
	margin_leverage?: number;
	details_section?: string;
	info_type?: string;
}

export interface SegmentBaseProperties {
	properties?: SegmentDynamicProperties;
}

export interface SegmentTrackProperties extends SegmentBaseProperties {
	name: string;
}

interface SegmentIdentifyProperties extends SegmentBaseProperties {
	id: string;
}

interface SegmentPageProperties extends SegmentBaseProperties {
	name: string;
	overridePath?: string;
}

@Injectable({
	providedIn: "root"
})
export class SegmentService {
	private previousPath: string = "";
	private currentPath: string = "";

	public constructor(private router: Router) {
		this.currentPath = this.router.url;
		// Getting the active and previous route from ng-router so we can track referrer
		router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.previousPath = this.currentPath;
				this.currentPath = event.url;
			}
		});
	}

	public page(properties: SegmentPageProperties): void {
		// Check if analytics is loaded -> cookie banner
		if (!window.analytics) {
			return;
		}

		// OverridePath is mainly used for modals, we need "fake" urls so that tracking makes sense
		const path = properties.overridePath || window.location.pathname;
		const url = properties.overridePath ? window.location.origin + properties.overridePath : window.location.href;

		// Segment analytics call, name is mandatory
		window.analytics.page(properties.name, {
			...properties.properties, // Optional properties
			...{
				// Required properties
				path,
				url,
				referrer: this.previousPath
			}
		});

		// Override previous path of angular router with fake route
		if (properties.overridePath) {
			this.previousPath = path;
		}
	}

	public track(data: SegmentTrackProperties): void {
		// Check if analytics is loaded -> cookie banner
		if (!window.analytics) {
			return;
		}

		// Track event name and optional properties
		window.analytics.track(data.name, data.properties);
	}

	public identify(data: SegmentIdentifyProperties): void {
		// Check if analytics is loaded -> cookie banner
		if (!window.analytics) {
			return;
		}

		// Identify user with backedn id and optional properties
		// Used at user login
		window.analytics.identify(data.id, data.properties);
	}

	public reset(): void {
		// Check if analytics is loaded -> cookie banner
		if (!window.analytics) {
			return;
		}

		// Removes cookies which were set by segment analytics
		// Used at user logout
		window.analytics.reset();
	}
}
